import React, { useEffect, useState } from 'react';
import { apiFetch } from '../api';
import './AdminPanel.css';
import AdminSidebar from './AdminSidebar';
import RoleManagement from './RoleManagement';
import UserEdit from './UserEdit';
import LobbyEditor from './LobbyEditor';
import ClubEditor from './ClubEditor';
import AvatarModeration from './AvatarModeration';
import { canAccessAdmin, canAccessClubManager, canAdminAllClubs, canChangeGlobalSettings, hasPermission } from '../utils/permissions';
import { resizeImageFile } from '../utils/avatarImage';
import { formatRoleLabel } from '../utils/roleManagement';
import { formatDateTimeEST } from '../utils/dateTime';
import {
  MAX_PASSWORD_LENGTH,
  MAX_USERNAME_LENGTH,
  MIN_PASSWORD_LENGTH,
  validatePassword,
  validateUsername,
} from '../utils/credentials';

const ADMIN_SIDEBAR_WIDTH = 250;

function SiteSettingsSection() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const [botAvatarUrl, setBotAvatarUrl] = useState(null);
  const [botAvatarRotate, setBotAvatarRotate] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch('/admin/site-settings');
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || 'Failed to load settings');
      setBotAvatarUrl(data.settings?.bot_avatar_url || null);
      setBotAvatarRotate(data.settings?.bot_avatar_rotate !== false);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const saveRotate = async (checked) => {
    setBotAvatarRotate(checked);
    try {
      const res = await apiFetch('/admin/site-settings', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ bot_avatar_rotate: checked }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to save');
    } catch (err) {
      setError(err.message);
    }
  };

  if (loading) return <p>Loading site settings…</p>;

  return (
    <div className="site-settings-section">
      <h2>Site settings</h2>
      <p className="add-user-hint">Site-wide defaults used by all site bots at tables.</p>
      {error && <div className="admin-form-message admin-form-message--error">{error}</div>}
      {message && <div className="admin-form-message admin-form-message--success">{message}</div>}
      <div className="user-options-avatar-block">
        <h3>Default bot avatar</h3>
        <div className="user-options-avatar-preview">
          {botAvatarUrl ? (
            <img src={botAvatarUrl} alt="" className="user-options-avatar-img" />
          ) : (
            <span className="user-options-avatar-placeholder">B</span>
          )}
        </div>
        <label className="user-options-checkbox">
          <input
            type="checkbox"
            checked={botAvatarRotate}
            onChange={(e) => saveRotate(e.target.checked)}
          />
          <span>Auto-rotate bot avatar at table</span>
        </label>
        <label className="user-options-file">
          <span>Upload bot avatar</span>
          <input
            type="file"
            accept="image/png,image/jpeg,image/gif,image/webp"
            disabled={busy}
            onChange={async (e) => {
              const file = e.target.files?.[0];
              if (!file) return;
              setBusy(true);
              setError(null);
              try {
                const resized = await resizeImageFile(file);
                const fd = new FormData();
                fd.append('avatar', resized);
                const res = await apiFetch('/admin/site-settings/bot-avatar', { method: 'POST', body: fd });
                const data = await res.json();
                if (!res.ok) throw new Error(data.message || 'Upload failed');
                setBotAvatarUrl(data.settings?.bot_avatar_url || null);
                setMessage(data.message || 'Bot avatar updated');
                setTimeout(() => setMessage(null), 3500);
              } catch (err) {
                setError(err.message);
              } finally {
                setBusy(false);
                e.target.value = '';
              }
            }}
          />
        </label>
      </div>
    </div>
  );
}

function sanitizeUsernameInput(value) {
  return String(value ?? '').replace(/[^A-Za-z0-9._-]/g, '').slice(0, MAX_USERNAME_LENGTH);
}

function AdminPanel({
  adminSidebarOpen,
  toggleAdminSidebar,
  permissions = [],
  userRole = '',
}) {
  const [users, setUsers] = useState([]);
  const [editingUserId, setEditingUserId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const [assignableRoles, setAssignableRoles] = useState([]);
  const [rolesLoading, setRolesLoading] = useState(false);
  const [newUser, setNewUser] = useState({ username: '', password: '', role: '' });
  const [activeSection, setActiveSection] = useState('userManagement');
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);

  const authorized = canAccessAdmin(permissions, userRole);

  const fetchUsers = async (page) => {
    try {
      const response = await apiFetch(`/admin/users?page=${page}`);
      if (response.ok) {
        const result = await response.json();
        setUsers(result.users || []);
        setTotalPages(result.totalPages || 1);
        setTotalUsers(result.totalUsers || 0);
      } else {
        console.error('Failed to fetch users:', response.statusText);
      }
    } catch (err) {
      console.error('Error fetching users:', err);
    }
  };

  useEffect(() => {
    if (authorized && activeSection === 'userManagement') {
      fetchUsers(currentPage);
    }
  }, [currentPage, activeSection, authorized]);

  const canAddUser = hasPermission(permissions, userRole, 'add_user');
  const canEditUser = hasPermission(permissions, userRole, 'edit_user');
  const canDeleteUser = hasPermission(permissions, userRole, 'delete_user');
  const canManageLobbies = hasPermission(permissions, userRole, 'manage_lobbies');
  const canManageClubs = canAccessClubManager(permissions, userRole);
  const canCreateClubs = canAdminAllClubs(permissions, userRole);
  const canGlobalSettings = canChangeGlobalSettings(permissions, userRole);

  const openEditUser = (user) => {
    if (!user?.can_edit) return;
    setEditingUserId(user.id);
    setActiveSection('editUser');
    setError(null);
    setSuccess(null);
  };

  const closeEditUser = () => {
    setEditingUserId(null);
    setActiveSection('userManagement');
  };

  useEffect(() => {
    const fetchRoles = async () => {
      setRolesLoading(true);
      try {
        const response = await apiFetch('/admin/roles/assignable');
        if (response.ok) {
          const result = await response.json();
          const roles = result.roles || result;
          setAssignableRoles(roles);
          if (roles.length > 0) {
            setNewUser((prev) => ({ ...prev, role: roles[0].role_id }));
          } else {
            setNewUser((prev) => ({ ...prev, role: '' }));
          }
        } else {
          setAssignableRoles([]);
          setNewUser((prev) => ({ ...prev, role: '' }));
        }
      } catch (err) {
        console.error('Error fetching roles:', err);
        setAssignableRoles([]);
      } finally {
        setRolesLoading(false);
      }
    };

    if (activeSection === 'addUser' && canAddUser) {
      fetchRoles();
    }
  }, [activeSection, canAddUser]);

  const handleAddUser = async (e) => {
    e.preventDefault();
    if (!canAddUser || assignableRoles.length === 0) return;

    setError(null);
    setSuccess(null);

    if (!newUser.username || !newUser.password || !newUser.role) {
      setError('Please provide username, password, and select a role');
      return;
    }

    const usernameError = validateUsername(newUser.username);
    if (usernameError) {
      setError(usernameError);
      return;
    }

    const passwordError = validatePassword(newUser.password);
    if (passwordError) {
      setError(passwordError);
      return;
    }

    try {
      const response = await apiFetch('/admin/users', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newUser),
      });
      const result = await response.json();

      if (response.ok) {
        setSuccess('User added successfully');
        setNewUser({
          username: '',
          password: '',
          role: assignableRoles[0]?.role_id ?? '',
        });
        fetchUsers(currentPage);
      } else {
        setError(result.message || 'Error adding user');
      }
    } catch (err) {
      setError('Error adding user');
      console.error(err);
    }
  };

  const nextPage = () => setCurrentPage((prev) => (prev < totalPages ? prev + 1 : prev));
  const prevPage = () => setCurrentPage((prev) => (prev > 1 ? prev - 1 : prev));

  const openDeleteModal = (user) => {
    if (!user?.can_delete) return;
    setUserToDelete(user);
    setIsDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setUserToDelete(null);
  };

  const confirmDeleteUser = async () => {
    if (!userToDelete) return;
    try {
      const response = await apiFetch(`/admin/users/${userToDelete.id}`, { method: 'DELETE' });
      const result = await response.json().catch(() => ({}));
      if (response.ok) {
        setUsers(users.filter((user) => user.id !== userToDelete.id));
        setSuccess(`User ${userToDelete.username} deleted successfully`);
        fetchUsers(currentPage);
      } else {
        setError(result.message || 'Error deleting user');
      }
    } catch (err) {
      console.error('Error deleting user:', err);
      setError('Error deleting user');
    } finally {
      closeDeleteModal();
    }
  };

  if (!authorized) {
    return <div className="admin-forbidden">You are not authorized to view this page.</div>;
  }

  return (
    <div className="admin-panel-container">
      <AdminSidebar
        activeSection={activeSection}
        setActiveSection={setActiveSection}
        isOpen={adminSidebarOpen}
        toggleSidebar={toggleAdminSidebar}
        permissions={permissions}
        userRole={userRole}
      />

      <div
        className="admin-panel-content"
        style={{ marginLeft: adminSidebarOpen ? ADMIN_SIDEBAR_WIDTH : 0 }}
      >
        <h1>Admin Panel</h1>

        {activeSection === 'userManagement' && (
          <div className="user-management-section">
            <h2>User Management</h2>
            <p>Total Users: {totalUsers}</p>

            <div className="user-list">
              <table>
                <thead>
                  <tr>
                    <th>Username</th>
                    <th>Role</th>
                    <th>Last Login</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.length > 0 ? (
                    users.map((user) => (
                      <tr key={user.id}>
                        <td>{user.username}</td>
                        <td>{user.role}</td>
                        <td>{formatDateTimeEST(user.last_login)}</td>
                        <td>{user.is_enabled ? 'Active' : 'Disabled'}</td>
                        <td className="user-actions-cell">
                          <button
                            type="button"
                            className="user-action-btn"
                            disabled={!canEditUser || !user.can_edit}
                            title={
                              !canEditUser
                                ? 'You do not have permission to edit users'
                                : user.can_edit
                                  ? 'Edit user'
                                  : 'You can only edit users below your level'
                            }
                            onClick={() => openEditUser(user)}
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            className="user-action-btn user-action-btn--danger"
                            disabled={!canDeleteUser || !user.can_delete}
                            title={
                              !canDeleteUser
                                ? 'You do not have permission to delete users'
                                : user.can_delete
                                  ? 'Delete user'
                                  : 'You can only delete users below your level'
                            }
                            onClick={() => openDeleteModal(user)}
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="5">No users found.</td>
                    </tr>
                  )}
                </tbody>
              </table>

              <div className="pagination-controls">
                <button type="button" onClick={prevPage} disabled={currentPage === 1}>
                  Previous
                </button>
                <span>Page {currentPage} of {totalPages}</span>
                <button type="button" onClick={nextPage} disabled={currentPage >= totalPages}>
                  Next
                </button>
              </div>
            </div>
          </div>
        )}

        {activeSection === 'editUser' && canEditUser && editingUserId != null && (
          <UserEdit
            userId={editingUserId}
            onBack={closeEditUser}
            onSaved={() => fetchUsers(currentPage)}
          />
        )}

        {activeSection === 'addUser' && canAddUser && (
          <div className="add-user-section">
            <h2>Add New User</h2>
            <p className="add-user-hint">
              Usernames: letters, numbers, dots, underscores, hyphens — max {MAX_USERNAME_LENGTH} chars, no spaces.
              Passwords: {MIN_PASSWORD_LENGTH}–{MAX_PASSWORD_LENGTH} characters.
            </p>
            {error && <div className="admin-form-message admin-form-message--error">{error}</div>}
            {success && <div className="admin-form-message admin-form-message--success">{success}</div>}
            <div className="add-user-panel">
              <form className="add-user-form" onSubmit={handleAddUser}>
                <label className="add-user-field">
                  <span className="add-user-label">Username</span>
                  <input
                    type="text"
                    placeholder="Enter username (no spaces)"
                    value={newUser.username}
                    onChange={(e) => setNewUser({
                      ...newUser,
                      username: sanitizeUsernameInput(e.target.value),
                    })}
                    autoComplete="off"
                    maxLength={MAX_USERNAME_LENGTH}
                    pattern="[A-Za-z0-9._-]+"
                    title="Letters, numbers, dots, underscores, and hyphens only"
                  />
                </label>
                <label className="add-user-field">
                  <span className="add-user-label">Password</span>
                  <input
                    type="password"
                    placeholder="Enter password"
                    value={newUser.password}
                    onChange={(e) => setNewUser({
                      ...newUser,
                      password: e.target.value.slice(0, MAX_PASSWORD_LENGTH),
                    })}
                    autoComplete="new-password"
                    maxLength={MAX_PASSWORD_LENGTH}
                  />
                </label>
                <label className="add-user-field">
                  <span className="add-user-label">Role</span>
                  {rolesLoading ? (
                    <div className="add-user-role-placeholder">Loading roles…</div>
                  ) : assignableRoles.length > 0 ? (
                    <select
                      value={newUser.role}
                      onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                    >
                      {assignableRoles.map((role) => (
                        <option key={role.role_id} value={role.role_id}>
                          {formatRoleLabel(role.role_name)}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <div className="add-user-role-placeholder" aria-disabled="true">
                      (No roles available)
                    </div>
                  )}
                </label>
                <button
                  type="submit"
                  className="add-user-submit"
                  disabled={rolesLoading || assignableRoles.length === 0}
                >
                  Add User
                </button>
              </form>
            </div>
          </div>
        )}

        {activeSection === 'roleManagement' && <RoleManagement />}
        {activeSection === 'lobbyEditor' && canManageLobbies && <LobbyEditor />}
        {activeSection === 'clubManager' && canManageClubs && (
          <ClubEditor canAdminAllClubs={canCreateClubs} />
        )}
        {activeSection === 'avatarModeration' && <AvatarModeration />}
        {activeSection === 'siteSettings' && canGlobalSettings && <SiteSettingsSection />}
      </div>

      {isDeleteModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content admin-delete-modal">
            <button type="button" className="close-button" onClick={closeDeleteModal}>X</button>
            <h2>Confirm Delete</h2>
            <p>Are you sure you want to delete user {userToDelete?.username}?</p>
            <div className="admin-delete-actions">
              <button type="button" className="user-action-btn user-action-btn--danger" onClick={confirmDeleteUser}>
                Yes, Delete
              </button>
              <button type="button" className="user-action-btn" onClick={closeDeleteModal}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminPanel;